<!--
 * @Author: 张天昊
 * @Date: 2024-05-24 14:32:04
 * @LastEditTime: 2024-06-06 14:37:25
 * @LastEditors: 张天昊
 * @Description: 车辆卡片
 * @FilePath: /Mobile/src/pages/itineraryUniversal/components/VehicleCard.vue
-->
<script>
import { defineComponent, computed } from 'vue';
import CardContainer from './CardContainer.vue';
import Description from './Description.vue';
import WithPhone from './WithPhone.vue';
import FileCell from './FileCell.vue';
import { useCardContainerEl } from '../composables/useCardContainerEl';
import { useFilePreview } from '../composables/useFilePreview';
import {
  TrafficStateTypes,
  TrafficTypes,
  TrafficConfirmStateType
} from '../constants';
import { transformEnumValue2Label } from '../utils';
import { FilePreviewer } from '@/components/FilePreviewer';
import imgBusPlaceholder from '@/assets/itineraryUniversal/bus-placeholder.png';
import imgTrainPlaceholder from '@/assets/itineraryUniversal/train-placeholder.png';
import imgShipPlaceholder from '@/assets/itineraryUniversal/ship-placeholder.png';
import imgPlanePlaceholder from '@/assets/itineraryUniversal/plane-placeholder.png';

export default defineComponent({
  components: {
    CardContainer,
    Description,
    WithPhone,
    FilePreviewer,
    FileCell
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props, { expose }) {
    const colorPlate = {
      黄色: '#FF9F3F',
      绿色: '#36B374'
    };
    const computedColorPlate = computed(() => {
      if (props.data.plateColor in colorPlate) {
        return {
          color: colorPlate[props.data.plateColor]
        };
      } else {
        return {
          color: '#f0f2f5',
          textColor: '#71747a'
        };
      }
    });

    const TrafficStateTypesValue2Label =
      transformEnumValue2Label(TrafficStateTypes);
    const TrafficConfirmStateTypeValue2Label = transformEnumValue2Label(
      TrafficConfirmStateType
    );

    const { container } = useCardContainerEl();
    const isBus = computed(() =>
      [TrafficTypes.客车, TrafficTypes.小包团用车].includes(
        props.data.trafficType
      )
    );
    const imgPlaceholder = computed(() => {
      if (isBus.value) {
        return imgBusPlaceholder;
      }
      if (props.data.trafficType === TrafficTypes.飞机) {
        return imgPlanePlaceholder;
      }
      if (props.data.trafficType === TrafficTypes.轮船) {
        return imgShipPlaceholder;
      }
      return imgTrainPlaceholder;
    });
    const notBusSubTitle = computed(() => {
      if (props.data.trafficType === TrafficTypes.飞机) {
        return `航班号：${props.data.trafficNumber}`;
      }
      if (props.data.trafficType === TrafficTypes.轮船) {
        return `船号：${props.data.trafficNumber}`;
      }
      if (props.data.trafficType === TrafficTypes.火车) {
        return `车次：${props.data.trafficNumber}`;
      }
      return '';
    });
    const notBusInfoLabels = computed(() => {
      const labels = [];
      if (props.data.trafficType === TrafficTypes.飞机) {
        labels.push('出发机场', '到达机场');
      }
      if (props.data.trafficType === TrafficTypes.轮船) {
        labels.push('出发港口', '到达港口');
      }
      if (props.data.trafficType === TrafficTypes.火车) {
        labels.push('出发站', '到达站');
      }
      labels.push('是否中转');
      return labels;
    });
    const notBusInfoValues = computed(() => {
      return [
        props.data.teamStartPlace,
        props.data.teamEndPlace,
        props.data.isTransfer ? '是' : '否'
      ];
    });
    const busTypeStr = computed(() => {
      return props.data.trafficType === TrafficTypes.小包团用车
        ? '小包团用车'
        : props.data.trafficTypeStr;
    });

    const { filePreviewerInst, onPreview } = useFilePreview();

    function onExternalLink() {
      // TODO 跳行程轨迹
      // window.yyn.invokeJSB({
      //   openUrl: '',
      //   needLoading: 1
      // })
    }

    expose({
      bid: props.data.id,
      toggleHighlight: () => {
        if (container.value) {
          container.value.toggleHighlight();
        }
      }
    });
    return {
      container,
      computedColorPlate,
      TrafficTypes,
      isBus,
      notBusSubTitle,
      notBusInfoLabels,
      notBusInfoValues,
      busTypeStr,
      TrafficStateTypesValue2Label,
      TrafficConfirmStateTypeValue2Label,
      imgPlaceholder,
      onExternalLink,
      filePreviewerInst,
      onPreview
    };
  }
});
</script>

<template>
  <CardContainer ref="container" bordered>
    <div class="relative">
      <div class="flex">
        <van-image
          :width="$px2rem(48)"
          :height="$px2rem(48)"
          :radius="$px2rem(4)"
          :error-icon="imgPlaceholder"
          src=""
          class="flex-shrink-0 custom-avatar"
          fit="cover"
        />
        <div class="ml-13">
          <div class="font-semibold text-0">
            <span class="align-middle text-18 leading-24"
              ><template v-if="isBus"
                >{{ busTypeStr }} · {{ data.carPlate }}</template
              ><template v-else>{{ data.trafficTypeStr }}</template></span
            >
            <van-tag
              v-if="isBus"
              :style="{ lineHeight: $px2rem(20) }"
              :color="computedColorPlate.color"
              :text-color="computedColorPlate.textColor"
              class="align-middle ml-6"
              >{{ data.plateColor }}⻋牌</van-tag
            >
          </div>
          <div class="mt-5 text-14 leading-20 text-black-b">
            {{ isBus ? data.motorCorporationName : notBusSubTitle }}
          </div>
        </div>
      </div>
      <van-divider class="custom-divider" />
      <template v-if="isBus">
        <Description class="mt-8" label="核载人数" :content="`${data.seat}`" />
        <Description class="mt-8" label="服务路段" :content="data.routeName" />
        <Description
          class="mt-8"
          label="驾驶员姓名"
          :content="data.driverName"
        />
        <Description class="mt-8" label="联系电话">
          <WithPhone
            :phone-number="data.driverPhone"
            class="text-black-a"
          ></WithPhone>
        </Description>
        <template v-if="data.trafficType !== TrafficTypes.小包团用车">
          <Description
            class="mt-8"
            label="确认状态"
            :content="
              data.status
                ? `${TrafficConfirmStateTypeValue2Label[data.status]}`
                : ''
            "
          />
          <Description
            class="mt-8"
            label="备案状态"
            :content="TrafficStateTypesValue2Label[`${data.recordsState}`]"
          />
        </template>
        <template v-else-if="data.attachment">
          <!-- 小包团附件 -->
          <van-divider :style="{ margin: `${$px2rem(15)} 0` }" />
          <div class="text-14 leading-20 font-medium">附件</div>
          <FileCell
            v-for="url in data.attachment.split(',')"
            :key="url"
            :url="url"
            class="mt-16 first:mt-12"
            @click="onPreview"
          />
        </template>
        <!-- <van-tag
          class="track-link z-1 bottom-0 right-0 van-haptics-feedback"
          color="#F0F2F5"
          text-color="#71747A"
          @click.stop="onExternalLink"
          >行车轨迹</van-tag
        > -->
      </template>
      <template v-else>
        <Description
          v-for="(label, index) in notBusInfoLabels"
          :key="label"
          :label="label"
          :content="notBusInfoValues[index]"
          class="mt-8"
        />
      </template>
    </div>
    <FilePreviewer ref="filePreviewerInst" />
  </CardContainer>
</template>

<style scoped>
.custom-divider {
  margin: 14px 0 10px;
}
.track-link.van-tag {
  position: absolute;
  line-height: 28px;
  font-size: 14px;
  padding: 0 8px;
}
</style>
